import { Box, Button, Divider, Stack, styled, Typography } from '@mui/material';
import { format } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AttachmentBox from '../../../components/AttachmentBox/AttachmentBox';
import DetailsCard from '../../../components/DetailsCard/DetailsCard';
import FormatCurrency from '../../../components/FormatNumber/FormatCurrency';
import PageTitle from '../../../components/PageTitle/PageTitle';
import TypographyDash from '../../../components/TypographyDash/TypographyDash';
import { dashBorder } from '../../../constants';
import { InvoiceStatus } from '../../../enums/invoiceStatus';
import { Invoice } from '../../../models/invoice';
import { PROJECTS } from '../../../router/constants';
import { fileService } from '../../../services/fileService';
import { invoiceService } from '../../../services/invoiceService';
import calculateInvoiceTotal from '../../../utils/invoiceTotal';
import ApproveInvoiceModal from './ApproveInvoiceModal';
import RejectInvoiceModal from './RejectInvoiceModal';

const BoldText = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  color: theme.palette.grey['900']
}));

const InvoiceDetails = (): JSX.Element => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [invoice, setInvoice] = useState<Invoice | undefined>(undefined);
  const [openRejectModal, setOpenRejectModal] = useState(false);
  const [openPayModal, setOpenPayModal] = useState(false);
  const [isFlatFee, setIsFlatFee] = useState(false);

  useEffect(() => {
    getInvoice();
  }, []);

  const getInvoice = () => {
    if (id) {
      invoiceService.getInvoiceById(+id).then((response) => {
        setInvoice(response.data);
      });
    }
  };

  const downloadAttachment = (uuid: string) => {
    fileService.getFileUrl(uuid).then((response) => {
      window.open(response.data, 'blank');
    });
  };

  useEffect(() => {
    if (!invoice?.totalHours && !invoice?.hourlyRate && invoice?.total) {
      setIsFlatFee(true);
    }
  }, [invoice]);

  return (
    <>
      <PageTitle>Invoice Details</PageTitle>
      <DetailsCard
        title={`Invoice #${id}`}
        subtitle={
          invoice
            ? `Submitted on: ${format(
                new Date(invoice.createdOn),
                'MMM do, Y'
              )}`
            : ''
        }
        status={invoice ? InvoiceStatus[invoice?.status] : undefined}
      >
        <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column' }}>
          <Box
            sx={{
              ...dashBorder,
              display: 'flex',
              gap: 3,
              flexDirection: { xs: 'column', lg: 'row' }
            }}
          >
            <Box sx={{ flex: '1 1 0px' }}>
              <Typography variant="h6" color="grey.900" mb={2}>
                PO #{invoice?.poNumber ?? '--'}
              </Typography>

              <Box display="flex" gap={3}>
                <Stack spacing={1} sx={{ flexShrink: 0 }}>
                  <BoldText>Project Title:</BoldText>
                  <BoldText>Brand:</BoldText>
                  <BoldText>Talent:</BoldText>
                  <BoldText>Category:</BoldText>
                  <BoldText>Expense Code:</BoldText>
                  <BoldText>Location:</BoldText>
                  <BoldText>Description:</BoldText>
                </Stack>
                <Stack spacing={1} sx={{ flex: '1 0 160px' }}>
                  {invoice?.projectId ? (
                    <TypographyDash
                      value={invoice?.projectTitle}
                      sx={{ color: 'primary.main', cursor: 'pointer' }}
                      onClick={() =>
                        navigate(`${PROJECTS}/${invoice?.projectId}`)
                      }
                    />
                  ) : (
                    <TypographyDash value={invoice?.projectTitle} />
                  )}
                  <TypographyDash value={invoice?.companyName} />
                  <TypographyDash value={invoice?.talentFullName} />
                  <TypographyDash value={invoice?.category} />
                  <TypographyDash value={invoice?.expenseCode} />
                  <TypographyDash value={invoice?.address} />
                  <TypographyDash value={invoice?.description} />
                </Stack>
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: '1 1 0px',
                alignItems: { xs: 'start', lg: 'end' }
              }}
            >
              <Box mb={3}>
                <Typography mb={1}>Project Attachments:</Typography>
                {invoice?.projectAttachments.map((file) => (
                  <AttachmentBox
                    key={file.id}
                    onClickFile={() => downloadAttachment(file.uuid)}
                    fileName={file.fileName}
                  />
                ))}
                {(!invoice || !invoice.projectAttachments.length) && (
                  <AttachmentBox fileName="No Attachments" />
                )}
              </Box>
              <Box>
                <Typography mb={1}>Talent Attachments:</Typography>
                {invoice?.attachments.map((file) => (
                  <AttachmentBox
                    key={file.id}
                    onClickFile={() => downloadAttachment(file.uuid)}
                    fileName={file.fileName}
                  />
                ))}
                {(!invoice || !invoice.attachments.length) && (
                  <AttachmentBox fileName="No Attachments" />
                )}
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              gap: 2,
              display: 'flex',
              flexDirection: { xs: 'column', lg: 'row' }
            }}
          >
            <Box
              sx={{
                ...dashBorder,
                flex: '1 1 0px'
              }}
            >
              <BoldText>Talent Notes:</BoldText>
              {invoice?.talentNotes}
            </Box>

            <Box
              sx={{
                ...dashBorder,
                flex: '1 1 0px',
                bgcolor: '#F6F7FB'
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: { sx: 'start', lg: 'end' },
                  gap: 2
                }}
              >
                {!isFlatFee && (
                  <>
                    <Box
                      sx={{
                        textAlign: { sx: 'start', lg: 'end' },
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-around'
                      }}
                    >
                      <Typography>Total Hours Worked:</Typography>
                      <Typography>Pay/Hour:</Typography>
                      <Typography>Expenses:</Typography>
                    </Box>
                    <Box textAlign="right">
                      <Box>
                        <BoldText
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'end'
                          }}
                        >
                          {invoice?.totalHours
                            ? `${invoice?.totalHours}h`
                            : '0'}
                        </BoldText>
                        <BoldText
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'end'
                          }}
                        >
                          <FormatCurrency value={invoice?.hourlyRate ?? 0} />
                        </BoldText>
                        <BoldText
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'end'
                          }}
                        >
                          <FormatCurrency value={invoice?.expenses ?? 0} />
                        </BoldText>
                      </Box>
                    </Box>
                  </>
                )}
                {isFlatFee && (
                  <Box width="100%">
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: { sx: 'start', lg: 'end' },
                        gap: 2
                      }}
                    >
                      <Box
                        sx={{
                          textAlign: { sx: 'start', lg: 'end' },
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-around'
                        }}
                      >
                        <Typography whiteSpace="nowrap">
                          Invoice Fee Total:
                        </Typography>
                        <Typography whiteSpace="nowrap">Expenses:</Typography>
                      </Box>
                      <Box>
                        <Box
                          textAlign="right"
                          sx={{
                            display: 'flex',
                            gap: 2,
                            flexDirection: 'column'
                          }}
                        >
                          <BoldText
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'end'
                            }}
                          >
                            <FormatCurrency value={invoice?.total ?? 0} />
                          </BoldText>
                          <BoldText
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'end'
                            }}
                          >
                            <FormatCurrency value={invoice?.expenses ?? 0} />
                          </BoldText>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>
              <Divider sx={{ my: 2 }} />
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: { sx: 'start', lg: 'end' },
                  gap: 2,
                  alignItems: 'center'
                }}
              >
                <Typography>Invoice Total:</Typography>
                <Typography variant="h5" color="grey.900" fontWeight="bold">
                  ${invoice ? calculateInvoiceTotal(invoice) : '--'}
                </Typography>
              </Box>
            </Box>
          </Box>
          {invoice?.status === 'SUBMITTED' && (
            <Stack
              direction="row"
              justifyContent="flex-end"
              alignItems="center"
              spacing={2}
              mt={1}
            >
              <Button
                variant="outlined"
                onClick={() => setOpenRejectModal(true)}
              >
                Reject
              </Button>
              <Button
                sx={{ width: '150px' }}
                onClick={() => setOpenPayModal(true)}
              >
                Pay
              </Button>
            </Stack>
          )}
        </Box>
      </DetailsCard>
      <RejectInvoiceModal
        openModal={openRejectModal}
        closeModal={() => setOpenRejectModal(false)}
        invoiceNumber={Number(id)}
        onConfirm={getInvoice}
      />
      <ApproveInvoiceModal
        openModal={openPayModal}
        closeModal={() => setOpenPayModal(false)}
        invoiceIds={[Number(id)]}
        onSuccess={getInvoice}
      />
    </>
  );
};

export default InvoiceDetails;
